import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";
import { API } from "../lib/api";
import { Chapter, FadeUp } from "./Reveal";

const BESOIN_CHOICES = [
  "Gros œuvre", "TP / réseaux", "Isolation", "Bois / panneaux", "Couverture / bardage",
  "Menuiserie", "Sols / carrelage", "Sanitaire / plomberie", "Électricité",
  "Aménagement extérieur", "Outillage / quincaillerie", "Autre",
];
const DELAI_CHOICES = [
  "Dès que possible", "Moins de 7 jours", "Dans le mois", "Projet à venir", "Je ne sais pas encore",
];

const INITIAL = {
  nom: "", societe: "", telephone: "", email: "", code_postal: "", ville: "",
  besoin: "", delai: "", description: "", deplacement: false, adresse: "",
  rgpd: false, website: "",
};

export const LeadForm = () => {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/leads`, { ...form, adresse: form.deplacement ? form.adresse : null });
      setSent(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Vérifiez les champs du formulaire puis réessayez."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="votre-besoin"
      data-testid="lead-form-section"
      className="bg-creme py-20 lg:py-32 scroll-mt-16"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Chapter num="07" label="Votre besoin" />
            <FadeUp>
              <h2 className="font-display font-medium text-petrol text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight mb-6">
                Parlons de votre chantier.
              </h2>
              <p className="text-base text-mute leading-relaxed mb-10">
                Professionnels, présentez en ligne vos besoins en matériaux de construction
                et de rénovation. Un commercial EQUORIS vous recontactera pour étudier votre
                demande et pourra organiser une visite sur site si nécessaire.
              </p>
              <div className="hidden lg:block w-32 h-32 rounded-t-full bg-sagelight" aria-hidden="true" />
            </FadeUp>
          </div>

          <FadeUp delay={0.15} className="lg:col-span-7">
            <div className="bg-white rounded-[2rem] border border-petrol/10 shadow-xl shadow-petrol/5 p-7 sm:p-10">
              {sent ? (
                <div data-testid="lead-form-success" className="text-center py-14">
                  <span className="mx-auto w-16 h-16 rounded-t-full rounded-b-2xl bg-sagelight flex items-center justify-center mb-6">
                    <CheckCircle2 className="w-8 h-8 text-teal" />
                  </span>
                  <h3 className="font-display font-medium text-petrol text-2xl sm:text-3xl mb-4">
                    Demande transmise.
                  </h3>
                  <p className="text-mute text-base max-w-md mx-auto leading-relaxed">
                    Votre demande a bien été transmise. Un commercial EQUORIS prendra contact
                    avec vous prochainement.
                  </p>
                  <button
                    onClick={() => { setForm(INITIAL); setSent(false); }}
                    data-testid="lead-form-reset-btn"
                    className="mt-8 text-sm font-semibold text-teal hover:text-petrol transition-colors duration-200 underline underline-offset-4"
                  >
                    Envoyer une autre demande
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="lead-form" noValidate={false}>
                  <input
                    type="text"
                    name="website"
                    value={form.website}
                    onChange={set("website")}
                    className="hidden"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="lead-nom" className="eq-label">Nom et prénom *</label>
                      <input id="lead-nom" data-testid="lead-nom" className="eq-input" value={form.nom} onChange={set("nom")} required />
                    </div>
                    <div>
                      <label htmlFor="lead-societe" className="eq-label">Société *</label>
                      <input id="lead-societe" data-testid="lead-societe" className="eq-input" value={form.societe} onChange={set("societe")} required />
                    </div>
                    <div>
                      <label htmlFor="lead-telephone" className="eq-label">Téléphone *</label>
                      <input id="lead-telephone" data-testid="lead-telephone" type="tel" className="eq-input" value={form.telephone} onChange={set("telephone")} required />
                    </div>
                    <div>
                      <label htmlFor="lead-email" className="eq-label">Email *</label>
                      <input id="lead-email" data-testid="lead-email" type="email" className="eq-input" value={form.email} onChange={set("email")} required />
                    </div>
                    <div>
                      <label htmlFor="lead-code-postal" className="eq-label">Code postal du chantier *</label>
                      <input id="lead-code-postal" data-testid="lead-code-postal" inputMode="numeric" className="eq-input" value={form.code_postal} onChange={set("code_postal")} required />
                    </div>
                    <div>
                      <label htmlFor="lead-ville" className="eq-label">Ville du chantier *</label>
                      <input id="lead-ville" data-testid="lead-ville" className="eq-input" value={form.ville} onChange={set("ville")} required />
                    </div>
                    <div>
                      <label htmlFor="lead-besoin" className="eq-label">Votre besoin *</label>
                      <select id="lead-besoin" data-testid="lead-besoin" className="eq-input" value={form.besoin} onChange={set("besoin")} required>
                        <option value="" disabled>Sélectionnez</option>
                        {BESOIN_CHOICES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="lead-delai" className="eq-label">Quand avez-vous besoin des matériaux ? *</label>
                      <select id="lead-delai" data-testid="lead-delai" className="eq-input" value={form.delai} onChange={set("delai")} required>
                        <option value="" disabled>Sélectionnez</option>
                        {DELAI_CHOICES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="mt-5">
                    <label htmlFor="lead-description" className="eq-label">Décrivez votre besoin *</label>
                    <textarea
                      id="lead-description"
                      data-testid="lead-description"
                      rows={4}
                      className="eq-input resize-y"
                      placeholder="Exemple : nature du chantier, matériaux recherchés, quantités, dimensions et contraintes particulières…"
                      value={form.description}
                      onChange={set("description")}
                      required
                    />
                  </div>

                  <div className="mt-6 space-y-4">
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="lead-deplacement-label">
                      <input
                        type="checkbox"
                        data-testid="lead-deplacement"
                        checked={form.deplacement}
                        onChange={set("deplacement")}
                        className="mt-0.5 w-4 h-4 rounded accent-[#1A6B78]"
                      />
                      <span className="text-sm text-encre/85">
                        Je souhaite qu'un commercial se déplace sur site
                      </span>
                    </label>
                    {form.deplacement && (
                      <div>
                        <label htmlFor="lead-adresse" className="eq-label">
                          Adresse du chantier / lieu de rendez-vous
                        </label>
                        <input
                          id="lead-adresse"
                          data-testid="lead-adresse"
                          className="eq-input"
                          value={form.adresse}
                          onChange={set("adresse")}
                        />
                      </div>
                    )}
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="lead-rgpd-label">
                      <input
                        type="checkbox"
                        data-testid="lead-rgpd"
                        checked={form.rgpd}
                        onChange={set("rgpd")}
                        required
                        className="mt-0.5 w-4 h-4 rounded accent-[#1A6B78]"
                      />
                      <span className="text-sm text-encre/85">
                        J'accepte que EQUORIS utilise les informations transmises afin de me
                        recontacter au sujet de ma demande. *
                      </span>
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="lead-form-submit"
                    className="mt-8 w-full rounded-full bg-petrol text-ivoire text-sm font-semibold px-7 py-4 inline-flex items-center justify-center gap-2 transition-all duration-300 hover:bg-teal disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {sending ? "Envoi en cours…" : "Envoyer ma demande à un commercial"}
                    {!sending && <Send className="w-4 h-4" />}
                  </button>
                </form>
              )}
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};
